import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@noor/lib/cn';

function BottomNav({ className, ...props }: React.ComponentProps<'nav'>) {
  return (
    <nav
      data-slot="bottom-nav"
      className={cn(
        'flex items-stretch justify-around rounded-t-3xl bg-card shadow-popover-halo',
        'pb-[env(safe-area-inset-bottom)]',
        className,
      )}
      {...props}
    />
  );
}

const bottomNavItemVariants = cva(
  [
    "flex h-[72px] flex-1 flex-col items-center justify-center gap-1.5 outline-none [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-6",
    // The label under the glyph (Figma Text/12/Medium, centered, never wrapping
    // — "Сотрудники" is the longest and must stay on one line at 5 tabs wide).
    '[&>span]:max-w-full [&>span]:truncate [&>span]:text-xs [&>span]:leading-none [&>span]:font-medium',
    // Focus ring inset so it stays inside the bar; color cross-fades with state.
    'transition-colors duration-200 ease-out focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring/40 active:scale-[0.97]',
    'transition-transform',
  ],
  {
    variants: {
      active: {
        // The design keeps the ACTIVE label near-black and the icon filled,
        // while inactive tabs go muted in both — so the label tracks
        // `text-foreground` on the active tab rather than the icon's primary.
        true: 'text-primary [&>span]:text-foreground',
        false: 'text-muted-foreground hover:text-foreground',
      },
    },
    defaultVariants: {
      active: false,
    },
  },
);

export type BottomNavItemProps = React.ComponentProps<'button'> &
  VariantProps<typeof bottomNavItemVariants> & {
    asChild?: boolean;
  };

function BottomNavItem({
  className,
  active = false,
  asChild = false,
  ...props
}: BottomNavItemProps) {
  const Comp = asChild ? Slot : 'button';
  return (
    <Comp
      data-slot="bottom-nav-item"
      data-active={active}
      aria-current={active ? 'page' : undefined}
      className={cn(bottomNavItemVariants({ active }), className)}
      {...props}
    />
  );
}

BottomNav.Item = BottomNavItem;

export { BottomNav, BottomNavItem, bottomNavItemVariants };
